import { useMemo, useState } from "react";

const MAX_TITLE_PX = 600;
const MAX_DESC_PX = 990; // ~160 chars at avg

// Rough pixel-width lookup for common latin chars (Arial 20px-ish).
const CHAR_WIDTHS: Record<string, number> = { i: 4, l: 4, I: 5, "|": 4, "!": 5, ".": 5, ",": 5, ";": 5, ":": 5, " ": 4 };
function pxWidth(text: string, base = 8) {
  return [...text].reduce((n, ch) => n + (CHAR_WIDTHS[ch] ?? base), 0);
}
function truncate(text: string, maxPx: number, base = 8) {
  if (pxWidth(text, base) <= maxPx) return { text, truncated: false };
  const words = text.split(" ");
  let out = "";
  for (const w of words) {
    const trial = out ? `${out} ${w}` : w;
    if (pxWidth(trial + "…", base) > maxPx) break;
    out = trial;
  }
  return { text: (out || text.slice(0, 40)) + "…", truncated: true };
}

export function SerpPreviewTool() {
  const [title, setTitle] = useState("The Complete Guide to SEO in 2026 — Optimize Plus");
  const [url, setUrl] = useState("https://optimizeplus.com/guides/the-complete-guide-to-seo-in-2026");
  const [desc, setDesc] = useState(
    "A pragmatic, end-to-end guide to organic search when AI Overviews, classical rankings, and local search all sit on the same page. Written by practitioners.",
  );
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");

  const t = useMemo(() => truncate(title, device === "desktop" ? MAX_TITLE_PX : 480, 8), [title, device]);
  const d = useMemo(() => truncate(desc, device === "desktop" ? MAX_DESC_PX : 780, 7), [desc, device]);

  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <div className="space-y-5">
        <Field label={`Title (${title.length} chars · ${pxWidth(title)}px)`}>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className={inp} />
        </Field>
        <Field label="URL">
          <input value={url} onChange={(e) => setUrl(e.target.value)} className={inp} />
        </Field>
        <Field label={`Meta description (${desc.length} chars)`}>
          <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={4} className={inp} />
        </Field>
        <div className="flex gap-2 pt-2">
          {(["desktop", "mobile"] as const).map((d) => (
            <button key={d} onClick={() => setDevice(d)} className={`rounded-full px-3 py-1 text-xs border ${device === d ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}>{d}</button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Preview · {device}</div>
        <div className={`rounded-2xl border border-border bg-white p-6 ${device === "mobile" ? "max-w-sm" : ""}`}>
          <div className="text-[13px] text-neutral-600 truncate">{url}</div>
          <div className="mt-1 text-[20px] leading-tight text-[#1a0dab] font-normal">{t.text}</div>
          <div className="mt-1 text-[14px] leading-snug text-neutral-800">{d.text}</div>
        </div>
        <div className="mt-4 text-xs text-muted-foreground space-y-1">
          {t.truncated && <div>· Title will be truncated on {device}.</div>}
          {d.truncated && <div>· Description will be truncated on {device}.</div>}
          {!t.truncated && !d.truncated && <div>· Fits within {device} limits.</div>}
        </div>
      </div>
    </div>
  );
}

export function MetaLengthTool() {
  const [text, setText] = useState(
`Home | Optimize Plus — Austin SEO agency
Services — Technical, Local, AEO/GEO
About Us | Optimize Plus`,
  );
  const rows = text.split("\n").filter(Boolean).map((line) => {
    const chars = line.length;
    const px = pxWidth(line);
    const status = px > 600 ? "over" : px > 540 ? "warn" : "ok";
    return { line, chars, px, status };
  });
  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr]">
      <Field label="Paste titles (or descriptions), one per line">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={12} className={inp} />
      </Field>
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Results</div>
        <div className="rounded-2xl border border-border divide-y divide-border">
          {rows.map((r, i) => (
            <div key={i} className="p-4 flex items-center gap-4">
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${r.status === "over" ? "bg-red-500/20 text-red-400" : r.status === "warn" ? "bg-amber-500/20 text-amber-400" : "bg-emerald-500/20 text-emerald-400"}`}>{r.status}</span>
              <span className="flex-1 truncate text-sm">{r.line}</span>
              <span className="text-xs text-muted-foreground tabular-nums">{r.chars} ch · {r.px}px</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ReadingGradeTool() {
  const [text, setText] = useState(
    "In 2026, the top of the SERP is often an AI-generated summary. Being cited in that summary now matters more than ranking #3 in the classical results below it. This is the whole argument for AEO/GEO.",
  );
  const stats = useMemo(() => {
    const sentences = text.split(/[.!?]+/).filter((s) => s.trim().length > 0);
    const words = text.split(/\s+/).filter(Boolean);
    const syllables = words.reduce((n, w) => n + Math.max(1, (w.toLowerCase().replace(/[^a-z]/g, "").match(/[aeiouy]+/g) ?? []).length), 0);
    const asl = words.length / Math.max(1, sentences.length);
    const asw = syllables / Math.max(1, words.length);
    const fk = 0.39 * asl + 11.8 * asw - 15.59;
    const passives = (text.match(/\b(is|are|was|were|be|been|being)\s+\w+ed\b/gi) ?? []).length;
    return {
      words: words.length, sentences: sentences.length,
      grade: Math.max(0, Math.round(fk * 10) / 10),
      asl: Math.round(asl * 10) / 10,
      passiveRatio: sentences.length ? Math.round((passives / sentences.length) * 100) : 0,
    };
  }, [text]);
  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <Field label="Paste a paragraph">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={10} className={inp} />
      </Field>
      <div className="grid grid-cols-2 gap-4 content-start">
        <Stat label="Grade level" value={stats.grade.toString()} hint={stats.grade > 12 ? "Simplify" : stats.grade > 9 ? "OK for B2B" : "Very readable"} />
        <Stat label="Avg sentence" value={`${stats.asl} words`} hint={stats.asl > 22 ? "Split sentences" : "Fine"} />
        <Stat label="Passive voice" value={`${stats.passiveRatio}%`} hint={stats.passiveRatio > 20 ? "Reduce" : "Fine"} />
        <Stat label="Word count" value={stats.words.toString()} hint={`${stats.sentences} sentences`} />
      </div>
    </div>
  );
}

const GEO_QUESTIONS = [
  { axis: "Answer clarity", q: "Does the page answer the target query in the first 100 words?" },
  { axis: "Answer clarity", q: "Is the answer structured (list, table, or clear paragraph)?" },
  { axis: "Entity strength", q: "Is Organization or Person schema present with sameAs links?" },
  { axis: "Entity strength", q: "Is the author named with a linked bio?" },
  { axis: "Topical placement", q: "Does the page sit inside a pillar/spoke cluster?" },
  { axis: "Topical placement", q: "Are there ≥5 internal links from related pages using descriptive anchors?" },
  { axis: "Freshness", q: "Is a visible last-updated date within the last 6 months?" },
  { axis: "Freshness", q: "Does the underlying data reflect the updated date?" },
];
export function GeoScoreTool() {
  const [answers, setAnswers] = useState<Record<number, 0 | 1 | 2 | 3>>({});
  const score = Object.values(answers).reduce<number>((n, v) => n + v, 0);
  const max = GEO_QUESTIONS.length * 3;
  const band = score >= 20 ? "citation-ready" : score >= 14 ? "needs rework" : "consolidate or retire";
  const bandColor = score >= 20 ? "text-emerald-400" : score >= 14 ? "text-amber-400" : "text-red-400";
  return (
    <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr]">
      <div className="space-y-4">
        {GEO_QUESTIONS.map((q, i) => (
          <div key={i} className="rounded-xl border border-border p-4">
            <div className="text-[10px] uppercase tracking-widest text-primary">{q.axis}</div>
            <div className="mt-1 text-sm text-foreground/90">{q.q}</div>
            <div className="mt-3 flex gap-2">
              {[0, 1, 2, 3].map((v) => (
                <button key={v} onClick={() => setAnswers((a) => ({ ...a, [i]: v as 0 | 1 | 2 | 3 }))}
                  className={`w-9 h-9 rounded-lg border text-sm ${answers[i] === v ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                  {v}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="sticky top-24 self-start rounded-2xl border border-border p-6 bg-card/50">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">GEO Matrix score</div>
        <div className="mt-3 font-display text-6xl tabular-nums">{score}<span className="text-2xl text-muted-foreground">/{max}</span></div>
        <div className={`mt-2 text-sm font-medium ${bandColor}`}>{band}</div>
        <p className="mt-4 text-xs text-muted-foreground leading-relaxed">
          Scores of 20+ generally earn AI-search citations within 30–90 days. 14–19 needs targeted
          rework — usually a top-of-page rewrite and schema additions. Below 14, consider
          consolidation.
        </p>
      </div>
    </div>
  );
}

export function RobotsTesterTool() {
  const [robots, setRobots] = useState(
`User-agent: *
Disallow: /admin/
Disallow: /*.json$
Allow: /admin/public/

User-agent: Googlebot
Allow: /
`);
  const [path, setPath] = useState("/admin/public/index.html");
  const [ua, setUa] = useState("Googlebot");

  const result = useMemo(() => {
    const blocks: { agents: string[]; rules: { type: "allow" | "disallow"; pattern: string }[] }[] = [];
    let current: (typeof blocks)[number] | null = null;
    robots.split("\n").forEach((raw) => {
      const line = raw.split("#")[0].trim();
      if (!line) { current = null; return; }
      const [k, ...rest] = line.split(":");
      const v = rest.join(":").trim();
      const key = k.toLowerCase();
      if (key === "user-agent") {
        if (!current) { current = { agents: [], rules: [] }; blocks.push(current); }
        current.agents.push(v);
      } else if (key === "allow" || key === "disallow") {
        if (!current) { current = { agents: ["*"], rules: [] }; blocks.push(current); }
        current.rules.push({ type: key, pattern: v });
      }
    });
    const applicable = blocks.filter((b) => b.agents.includes(ua) || b.agents.includes("*"));
    const rules = applicable.flatMap((b) => b.rules);
    const matches = rules.filter((r) => matchPattern(path, r.pattern));
    const longest = matches.sort((a, b) => b.pattern.length - a.pattern.length)[0];
    return { verdict: !longest ? "allowed (no rule)" : longest.type === "allow" ? "allowed" : "blocked", rule: longest };
  }, [robots, path, ua]);

  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <Field label="robots.txt">
        <textarea value={robots} onChange={(e) => setRobots(e.target.value)} rows={14} className={`${inp} font-mono text-xs`} />
      </Field>
      <div className="space-y-4">
        <Field label="Path to test"><input value={path} onChange={(e) => setPath(e.target.value)} className={inp} /></Field>
        <Field label="User-agent"><input value={ua} onChange={(e) => setUa(e.target.value)} className={inp} /></Field>
        <div className="rounded-2xl border border-border p-6 bg-card/50">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Verdict</div>
          <div className={`mt-2 font-display text-3xl ${result.verdict === "blocked" ? "text-red-400" : "text-emerald-400"}`}>
            {result.verdict}
          </div>
          {result.rule && (
            <div className="mt-3 text-xs text-muted-foreground">Matched rule: <code className="text-foreground">{result.rule.type}: {result.rule.pattern}</code></div>
          )}
        </div>
      </div>
    </div>
  );
}

function matchPattern(path: string, pattern: string) {
  if (!pattern) return false;
  const re = new RegExp(
    "^" +
      pattern
        .replace(/[.+?^${}()|[\]\\]/g, "\\$&")
        .replace(/\*/g, ".*")
        .replace(/\\\$$/, "$") +
      (pattern.endsWith("$") ? "" : ""),
  );
  return re.test(path);
}

const SCHEMAS: { name: string; description: string; json: object }[] = [
  { name: "Organization", description: "Base entity schema for the brand. Deploy on the About page.", json: { "@context": "https://schema.org", "@type": "Organization", name: "Optimize Plus", url: "https://optimizeplus.com", logo: "https://optimizeplus.com/logo.png", sameAs: ["https://www.linkedin.com/company/optimize-plus", "https://twitter.com/optimizeplus"] } },
  { name: "LocalBusiness", description: "For businesses with a physical or service-area presence.", json: { "@context": "https://schema.org", "@type": "LocalBusiness", name: "Optimize Plus", address: { "@type": "PostalAddress", streetAddress: "1431 Salem Meadow Cir", addressLocality: "Austin", addressRegion: "TX", postalCode: "78745", addressCountry: "US" }, geo: { "@type": "GeoCoordinates", latitude: 30.1943, longitude: -97.8231 }, telephone: "+1-737-321-0551", email: "info@optimizeplus.net", url: "https://optimizeplus.net" } },
  { name: "FAQPage", description: "Question/answer content, one entry per FAQ.", json: { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: [{ "@type": "Question", name: "What is AEO?", acceptedAnswer: { "@type": "Answer", text: "Answer Engine Optimization — optimising content to be cited by answer engines." } }] } },
  { name: "Article", description: "Blog posts, editorial content, guides.", json: { "@context": "https://schema.org", "@type": "Article", headline: "AI Overviews in 2026", author: { "@type": "Person", name: "M. Umair Mansha" }, datePublished: "2026-05-04", publisher: { "@type": "Organization", name: "Optimize Plus" } } },
  { name: "BreadcrumbList", description: "Category > subcategory > page hierarchy.", json: { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [{ "@type": "ListItem", position: 1, name: "Resources", item: "https://optimizeplus.com/resources" }, { "@type": "ListItem", position: 2, name: "Blog", item: "https://optimizeplus.com/blog" }] } },
  { name: "Product", description: "For ecommerce PDPs.", json: { "@context": "https://schema.org", "@type": "Product", name: "Sample Product", offers: { "@type": "Offer", price: "49.00", priceCurrency: "USD", availability: "https://schema.org/InStock" }, aggregateRating: { "@type": "AggregateRating", ratingValue: "4.7", reviewCount: "128" } } },
  { name: "HowTo", description: "Step-by-step instructional content.", json: { "@context": "https://schema.org", "@type": "HowTo", name: "How to run a technical SEO audit", step: [{ "@type": "HowToStep", name: "Crawl", text: "Run a full crawl with a modern crawler." }] } },
];
export function SchemaExplorerTool() {
  const [i, setI] = useState(0);
  const active = SCHEMAS[i];
  const [copied, setCopied] = useState(false);
  const pretty = JSON.stringify(active.json, null, 2);
  return (
    <div className="grid gap-8 lg:grid-cols-[240px_minmax(0,1fr)]">
      <div className="flex flex-col gap-1">
        {SCHEMAS.map((s, idx) => (
          <button key={s.name} onClick={() => { setI(idx); setCopied(false); }}
            className={`text-left rounded-lg px-3 py-2 text-sm border ${i === idx ? "bg-primary/10 border-primary/40 text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {s.name}
          </button>
        ))}
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Type</div>
        <h3 className="mt-1 font-display text-2xl">{active.name}</h3>
        <p className="mt-2 text-sm text-muted-foreground">{active.description}</p>
        <div className="mt-4 relative">
          <pre className="rounded-2xl border border-border bg-background/60 p-5 text-xs overflow-x-auto font-mono leading-relaxed">
{`<script type="application/ld+json">
${pretty}
</script>`}
          </pre>
          <button
            onClick={() => { navigator.clipboard?.writeText(pretty); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
            className="absolute top-3 right-3 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground"
          >
            {copied ? "✓ Copied" : "Copy JSON"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- shared bits ---------- */
const inp = "w-full rounded-xl bg-background border border-input px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{label}</div>
      {children}
    </label>
  );
}
function Stat({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="rounded-xl border border-border p-4">
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-2 font-display text-3xl">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{hint}</div>
    </div>
  );
}

/* ============================================================
 * Open Graph & Twitter card preview
 * ============================================================ */
export function OgPreviewTool() {
  const [title, setTitle] = useState("Austin SEO Agency & AI Search Experts | Optimize Plus");
  const [desc, setDesc] = useState("Founder-led Austin SEO agency delivering local SEO, technical SEO, and AI search optimisation for small businesses and B2B teams across Texas.");
  const [image, setImage] = useState("https://optimizeplus.net/og-default.jpg");
  const [url, setUrl] = useState("https://optimizeplus.net/");
  const [site, setSite] = useState("optimizeplus.net");
  const missing: string[] = [];
  if (!title) missing.push("og:title");
  if (!desc) missing.push("og:description");
  if (!image) missing.push("og:image");
  if (!url) missing.push("og:url");
  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr]">
      <div className="space-y-5">
        <Field label={`og:title (${title.length})`}><input value={title} onChange={(e) => setTitle(e.target.value)} className={inp} /></Field>
        <Field label={`og:description (${desc.length})`}><textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} className={inp} /></Field>
        <Field label="og:image (1200×630 recommended)"><input value={image} onChange={(e) => setImage(e.target.value)} className={inp} /></Field>
        <Field label="og:url / canonical"><input value={url} onChange={(e) => setUrl(e.target.value)} className={inp} /></Field>
        <Field label="Site name / domain"><input value={site} onChange={(e) => setSite(e.target.value)} className={inp} /></Field>
        {missing.length > 0 && (
          <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-400">
            Missing: {missing.join(", ")}
          </div>
        )}
      </div>
      <div className="space-y-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Facebook / LinkedIn</div>
          <div className="rounded-2xl overflow-hidden border border-border bg-white text-neutral-900">
            {image && <div className="aspect-[1.91/1] w-full bg-neutral-100 overflow-hidden"><img src={image} alt="" width={1200} height={628} loading="lazy" decoding="async" className="w-full h-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} /></div>}
            <div className="p-4">
              <div className="text-[11px] uppercase text-neutral-500 tracking-wider">{site}</div>
              <div className="mt-1 text-[16px] font-semibold leading-snug line-clamp-2">{title}</div>
              <div className="mt-1 text-[13px] text-neutral-600 line-clamp-2">{desc}</div>
            </div>
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">X (Twitter) — summary_large_image</div>
          <div className="rounded-2xl overflow-hidden border border-border bg-white text-neutral-900 max-w-md">
            {image && <div className="aspect-[1.91/1] w-full bg-neutral-100"><img src={image} alt="" width={1200} height={628} loading="lazy" decoding="async" className="w-full h-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} /></div>}
            <div className="p-3 border-t border-neutral-200">
              <div className="text-[13px] text-neutral-500">{site}</div>
              <div className="mt-0.5 text-[14px] font-medium leading-snug line-clamp-1">{title}</div>
              <div className="text-[13px] text-neutral-600 line-clamp-1">{desc}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 * UTM campaign URL builder
 * ============================================================ */
export function UtmBuilderTool() {
  const [url, setUrl] = useState("https://optimizeplus.net/services/local-seo/");
  const [source, setSource] = useState("google");
  const [medium, setMedium] = useState("cpc");
  const [campaign, setCampaign] = useState("austin_local_seo_q1");
  const [term, setTerm] = useState("");
  const [content, setContent] = useState("");
  const [copied, setCopied] = useState(false);
  const clean = (v: string) => v.trim().toLowerCase().replace(/\s+/g, "_");
  const built = (() => {
    if (!url) return "";
    try {
      const u = new URL(url);
      const set = (k: string, v: string) => v && u.searchParams.set(k, clean(v));
      set("utm_source", source);
      set("utm_medium", medium);
      set("utm_campaign", campaign);
      set("utm_term", term);
      set("utm_content", content);
      return u.toString();
    } catch {
      return "Invalid URL";
    }
  })();
  const warn: string[] = [];
  if (source && source !== clean(source)) warn.push("utm_source will be lowercased");
  if (campaign.includes(" ")) warn.push("Spaces in campaign name auto-convert to underscores");
  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <div className="space-y-4">
        <Field label="Destination URL"><input value={url} onChange={(e) => setUrl(e.target.value)} className={inp} /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="utm_source (required)"><input value={source} onChange={(e) => setSource(e.target.value)} className={inp} placeholder="google, newsletter, x" /></Field>
          <Field label="utm_medium (required)"><input value={medium} onChange={(e) => setMedium(e.target.value)} className={inp} placeholder="cpc, email, social" /></Field>
        </div>
        <Field label="utm_campaign (required)"><input value={campaign} onChange={(e) => setCampaign(e.target.value)} className={inp} /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="utm_term (optional)"><input value={term} onChange={(e) => setTerm(e.target.value)} className={inp} placeholder="paid keyword" /></Field>
          <Field label="utm_content (optional)"><input value={content} onChange={(e) => setContent(e.target.value)} className={inp} placeholder="ad-variant-a" /></Field>
        </div>
      </div>
      <div className="space-y-4">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Tagged URL</div>
        <div className="rounded-2xl border border-border bg-card/50 p-5">
          <div className="font-mono text-sm break-all text-foreground/90">{built || "—"}</div>
          <button
            onClick={() => { if (built && built !== "Invalid URL") { navigator.clipboard?.writeText(built); setCopied(true); setTimeout(() => setCopied(false), 1500); } }}
            className="mt-4 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground"
          >
            {copied ? "✓ Copied" : "Copy URL"}
          </button>
        </div>
        {warn.length > 0 && (
          <ul className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-300 space-y-1">
            {warn.map((w, i) => <li key={i}>· {w}</li>)}
          </ul>
        )}
        <p className="text-xs text-muted-foreground">Google Analytics normalises case, so we lowercase and underscore everything for consistency.</p>
      </div>
    </div>
  );
}

/* ============================================================
 * SEO slug generator
 * ============================================================ */
const STOP_WORDS = new Set(["a","an","the","and","or","but","of","in","on","at","to","for","with","by","from","is","are","was","were","be","been","being","this","that","these","those","it","its","as","how","why","what","when","where","who","which"]);
export function SlugGeneratorTool() {
  const [text, setText] = useState("How to Rank in Austin: The Complete Local SEO Playbook for 2026");
  const [stripStop, setStripStop] = useState(true);
  const [maxLen, setMaxLen] = useState(60);
  const [copied, setCopied] = useState(false);
  const slug = useMemo(() => {
    let s = text.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
    s = s.toLowerCase().replace(/['’"`]/g, "").replace(/[^a-z0-9\s-]/g, " ");
    let parts = s.split(/[\s-]+/).filter(Boolean);
    if (stripStop) parts = parts.filter((w) => !STOP_WORDS.has(w));
    let out = parts.join("-");
    if (out.length > maxLen) {
      const cut = out.slice(0, maxLen).split("-");
      cut.pop();
      out = cut.join("-");
    }
    return out;
  }, [text, stripStop, maxLen]);
  const warn = slug.length > maxLen - 5 ? "Slug is near the length limit — consider shortening." : slug.length < 3 ? "Slug is very short." : "";
  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <div className="space-y-4">
        <Field label="Headline or title"><textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} className={inp} /></Field>
        <label className="flex items-center gap-3 text-sm">
          <input type="checkbox" checked={stripStop} onChange={(e) => setStripStop(e.target.checked)} className="h-4 w-4 accent-primary" />
          Strip common stop-words (a, the, and, of…)
        </label>
        <Field label={`Max length: ${maxLen}`}>
          <input type="range" min={20} max={100} value={maxLen} onChange={(e) => setMaxLen(Number(e.target.value))} className="w-full accent-primary" />
        </Field>
      </div>
      <div className="space-y-4">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Generated slug</div>
        <div className="rounded-2xl border border-border bg-card/50 p-5 min-h-[100px]">
          <div className="font-mono text-lg text-primary break-all">/{slug || "…"}/</div>
          <div className="mt-2 text-xs text-muted-foreground">{slug.length} chars · {slug ? slug.split("-").length : 0} words</div>
        </div>
        <button
          onClick={() => { if (slug) { navigator.clipboard?.writeText(slug); setCopied(true); setTimeout(() => setCopied(false), 1500); } }}
          className="rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground"
        >
          {copied ? "✓ Copied" : "Copy slug"}
        </button>
        {warn && <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-300">{warn}</div>}
      </div>
    </div>
  );
}

/* ============================================================
 * Heading outline visualizer
 * ============================================================ */
export function HeadingOutlinerTool() {
  const [html, setHtml] = useState(
`<h1>The Complete Local SEO Playbook</h1>
<h2>Why local search is different</h2>
<h3>The Local Pack</h3>
<h2>Ranking factors that actually move the needle</h2>
<h4>Skipped level — this triggers a warning</h4>
<h2>Auditing an existing profile</h2>
<h3>NAP consistency</h3>`);
  const parsed = useMemo(() => {
    const re = /<h([1-6])[^>]*>([\s\S]*?)<\/h\1>/gi;
    const items: { level: number; text: string }[] = [];
    let m: RegExpExecArray | null;
    while ((m = re.exec(html))) {
      items.push({ level: Number(m[1]), text: m[2].replace(/<[^>]+>/g, "").trim() });
    }
    const warnings: string[] = [];
    const h1s = items.filter((i) => i.level === 1).length;
    if (h1s === 0) warnings.push("No H1 found.");
    if (h1s > 1) warnings.push(`Multiple H1 tags (${h1s}) — use exactly one.`);
    for (let i = 1; i < items.length; i++) {
      const jump = items[i].level - items[i - 1].level;
      if (jump > 1) warnings.push(`Level jumps from H${items[i - 1].level} → H${items[i].level} at "${items[i].text.slice(0, 40)}"`);
    }
    items.forEach((it, i) => { if (!it.text) warnings.push(`Empty H${it.level} at position ${i + 1}`); });
    return { items, warnings };
  }, [html]);
  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_1.1fr]">
      <Field label="Paste HTML (or a full article body)">
        <textarea value={html} onChange={(e) => setHtml(e.target.value)} rows={16} className={`${inp} font-mono text-xs`} />
      </Field>
      <div className="space-y-4">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Outline ({parsed.items.length} headings)</div>
        <div className="rounded-2xl border border-border bg-card/50 p-5 space-y-1.5">
          {parsed.items.length === 0 && <div className="text-sm text-muted-foreground">No headings detected.</div>}
          {parsed.items.map((h, i) => (
            <div key={i} className="flex items-start gap-3 text-sm" style={{ paddingLeft: (h.level - 1) * 16 }}>
              <span className="shrink-0 rounded bg-primary/15 text-primary text-[10px] font-mono px-1.5 py-0.5">H{h.level}</span>
              <span className="text-foreground/90">{h.text || <em className="text-red-400">(empty)</em>}</span>
            </div>
          ))}
        </div>
        {parsed.warnings.length > 0 ? (
          <ul className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-xs text-amber-300 space-y-1">
            {parsed.warnings.map((w, i) => <li key={i}>· {w}</li>)}
          </ul>
        ) : (
          <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 text-xs text-emerald-300">Outline looks clean.</div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
 * Keyword density analyzer
 * ============================================================ */
export function KeywordDensityTool() {
  const [text, setText] = useState(
    "Local SEO in Austin is more than a Google Business Profile. Local SEO combines on-page signals, local citations, review velocity, and technical health. In Austin, competition for local search terms grows quarterly, so a serious local SEO strategy pairs classical ranking work with AI-search readiness.",
  );
  const [n, setN] = useState<1 | 2 | 3>(1);
  const stats = useMemo(() => {
    const words = text.toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter(Boolean);
    const grams: Record<string, number> = {};
    for (let i = 0; i <= words.length - n; i++) {
      const g = words.slice(i, i + n).join(" ");
      if (n === 1 && STOP_WORDS.has(g)) continue;
      grams[g] = (grams[g] ?? 0) + 1;
    }
    const total = words.length;
    return {
      total,
      rows: Object.entries(grams).map(([k, v]) => ({ k, v, pct: (v / Math.max(1, total)) * 100 })).sort((a, b) => b.v - a.v).slice(0, 20),
    };
  }, [text, n]);
  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <div className="space-y-4">
        <Field label={`Text (${stats.total} words)`}>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={12} className={inp} />
        </Field>
        <div className="flex gap-2">
          {([1, 2, 3] as const).map((v) => (
            <button key={v} onClick={() => setN(v)} className={`rounded-full px-3 py-1 text-xs border ${n === v ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}>{v}-word</button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Top phrases</div>
        <div className="rounded-2xl border border-border divide-y divide-border overflow-hidden">
          {stats.rows.length === 0 && <div className="p-4 text-sm text-muted-foreground">No terms yet.</div>}
          {stats.rows.map((r) => {
            const flag = r.pct > 3;
            return (
              <div key={r.k} className="p-3 flex items-center gap-3">
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${flag ? "bg-red-500/20 text-red-400" : "bg-primary/15 text-primary"}`}>{r.pct.toFixed(1)}%</span>
                <span className="flex-1 truncate text-sm">{r.k}</span>
                <span className="text-xs text-muted-foreground tabular-nums">×{r.v}</span>
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">Anything above 3% may look manipulative. Aim for natural distribution.</p>
      </div>
    </div>
  );
}

/* ============================================================
 * Hreflang tag generator
 * ============================================================ */
const LANG_RE = /^[a-z]{2}(-[A-Z]{2})?$/;
export function HreflangGeneratorTool() {
  const [rows, setRows] = useState<{ lang: string; url: string }[]>([
    { lang: "en-US", url: "https://optimizeplus.net/" },
    { lang: "en-GB", url: "https://optimizeplus.net/en-gb/" },
    { lang: "es-MX", url: "https://optimizeplus.net/es-mx/" },
  ]);
  const [xDefault, setXDefault] = useState("https://optimizeplus.net/");
  const [copied, setCopied] = useState(false);
  const output = useMemo(() => {
    const lines = rows.filter((r) => r.lang && r.url).map((r) => `<link rel="alternate" hreflang="${r.lang}" href="${r.url}" />`);
    if (xDefault) lines.push(`<link rel="alternate" hreflang="x-default" href="${xDefault}" />`);
    return lines.join("\n");
  }, [rows, xDefault]);
  const warnings = rows.map((r, i) => (r.lang && !LANG_RE.test(r.lang) ? `Row ${i + 1}: "${r.lang}" isn't a valid xx or xx-XX code.` : "")).filter(Boolean);
  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <div className="space-y-4">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[100px_1fr_auto] gap-2">
            <input value={r.lang} onChange={(e) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, lang: e.target.value } : x))} placeholder="en-US" className={inp} />
            <input value={r.url} onChange={(e) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, url: e.target.value } : x))} placeholder="https://…" className={inp} />
            <button onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} aria-label="Remove" className="rounded-lg border border-border px-3 text-muted-foreground hover:text-red-400">×</button>
          </div>
        ))}
        <button onClick={() => setRows((rs) => [...rs, { lang: "", url: "" }])} className="rounded-full border border-border px-4 py-2 text-xs text-muted-foreground hover:text-foreground">+ Add locale</button>
        <Field label="x-default URL (fallback)"><input value={xDefault} onChange={(e) => setXDefault(e.target.value)} className={inp} /></Field>
      </div>
      <div className="space-y-4">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Output — paste in &lt;head&gt;</div>
        <div className="relative">
          <pre className="rounded-2xl border border-border bg-background/60 p-5 text-xs overflow-x-auto font-mono leading-relaxed whitespace-pre-wrap">{output || "—"}</pre>
          <button onClick={() => { if (output) { navigator.clipboard?.writeText(output); setCopied(true); setTimeout(() => setCopied(false), 1500); } }} className="absolute top-3 right-3 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">{copied ? "✓ Copied" : "Copy"}</button>
        </div>
        {warnings.length > 0 && (
          <ul className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-300 space-y-1">
            {warnings.map((w, i) => <li key={i}>· {w}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ============================================================
 * LocalBusiness schema generator
 * ============================================================ */
const DAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"] as const;
export function LocalBusinessSchemaTool() {
  const [f, setF] = useState({
    name: "Optimize Plus", type: "LocalBusiness",
    street: "1431 Salem Meadow Cir", city: "Austin", region: "TX", zip: "78745", country: "US",
    phone: "(737) 321-0551", url: "https://optimizeplus.net", email: "info@optimizeplus.net",
    lat: "30.1943", lng: "-97.8231",
    open: "00:00", close: "23:59", allDays: true,
    sameAs: "https://www.linkedin.com/company/optimize-plus\nhttps://www.facebook.com/optimizeplus",
    gmbId: "/g/11zb51w5c6",
    desc: "Optimize Plus is Austin's results-driven SEO company helping businesses in Austin, Round Rock, Cedar Park, Pflugerville, Georgetown, Leander, Buda, Kyle, Bee Cave, Bastrop, Dripping Springs, San Marcos, and nearby Texas areas grow online. We deliver Local SEO, Google Business Profile optimization, SEO audits, on-page/off-page SEO, link building, eCommerce SEO, AI search optimization, PPC, Google Ads, and conversion-focused web design — all backed by data-driven strategies that rank you higher on Google Search and Maps. With transparent reporting and flexible pay-after-results options, we turn visibility into real revenue for Texas businesses. Ready to get found and grow faster? Contact Optimize Plus today for a free SEO consultation!",
  });
  const [copied, setCopied] = useState(false);
  const json = useMemo(() => {
    const services = [
      "Internet marketing service", "Website designer", "Service establishment",
      "Marketing consultant", "Market researcher", "Branding agency",
      "Design agency", "Austin SEO Companies", "Austin SEO Expert",
      "Austin Web Design", "Brand Ads", "Brand Design", "Brand Development",
      "Brand Messaging", "Branding & Marketing", "Building Websites",
      "Built In SEO", "Business Development", "Campaign Reporting",
      "Campaign Setup", "Collaborative Design", "Color Adjustments",
      "Content Creation", "Content Marketing", "Content Strategy",
      "Content Writing", "Conversion Tracking", "Custom Product",
      "Digital Ads", "Digital Marketing", "Digital Marketing & Ppc",
      "Digital Marketing Agency", "Digital Products", "Digital Solutions",
      "Digital Strategy", "Drive Conversions", "E Commerce", "Facebook Campaign",
      "Full Service Agency", "Google Adwords Campaign", "Graphic Design",
      "Identity Branding", "Information Architecture", "Landing Page Creation",
      "Link Building", "Logo Design", "Make Web", "Marketing Agency",
      "Marketing And SEO", "Local SEO", "SEO Audit", "Technical SEO",
      "Off Page SEO", "On Page SEO", "Link Building Campaigns",
      "Local Citations Building", "E-commerce SEO", "Voice Search Optimization",
      "Keyword Optimization", "Map Optimization", "Google Business Profile",
      "Google Business Profile Optimization", "GBP Service", "GMB Ranking",
      "Google Ads", "PPC Management", "PPC Advertising", "PPC Landing Page Design",
      "Paid Media", "Web Design", "Website Design", "Web Design And Development",
      "Web Design And SEO", "Wordpress Development", "UI/UX Design",
      "Analytics and Reporting", "Digital Marketing Strategy", "CRO Audit",
      "Reputation Repair", "Austin SEO Services", "Local SEO Services"
    ];

    const obj: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": f.type,
      name: f.name,
      "@id": `${f.url}/#optimize-plus`,
      description: f.desc,
      url: f.url,
      telephone: f.phone,
      image: "https://lh4.googleusercontent.com/-TRH2fCnHTGM/AAAAAAAAAAI/AAAAAAAAAAA/vkoPWqKXXsE/s44-p-k-no-ns-nd/photo.jpg",
      address: {
        "@type": "PostalAddress",
        streetAddress: f.street,
        addressLocality: f.city,
        addressRegion: `${f.region} ${f.zip}`,
      },
      category: "Marketing agency",
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Services",
        itemListElement: services.map((name, i) => ({
          "@type": "Offer",
          itemOffered: { "@type": "Service", name },
          position: i + 1
        }))
      }
    };

    if (f.gmbId) {
      obj.identifier = {
        "@type": "PropertyValue",
        propertyID: "googleKgMID",
        value: f.gmbId
      };
    }
    const sameAs = f.sameAs.split("\n").map((s) => s.trim()).filter(Boolean);
    if (sameAs.length) obj.sameAs = sameAs;
    return JSON.stringify(obj, null, 2);
  }, [f]);
  const set = (k: keyof typeof f, v: string | boolean) => setF((p) => ({ ...p, [k]: v as never }));
  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Business name"><input value={f.name} onChange={(e) => set("name", e.target.value)} className={inp} /></Field>
          <Field label="Schema type">
            <select value={f.type} onChange={(e) => set("type", e.target.value)} className={inp}>
              {["LocalBusiness","Restaurant","Dentist","AutoRepair","HomeAndConstructionBusiness","ProfessionalService","Store"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Street"><input value={f.street} onChange={(e) => set("street", e.target.value)} className={inp} /></Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="City"><input value={f.city} onChange={(e) => set("city", e.target.value)} className={inp} /></Field>
          <Field label="Region"><input value={f.region} onChange={(e) => set("region", e.target.value)} className={inp} /></Field>
          <Field label="Postal code"><input value={f.zip} onChange={(e) => set("zip", e.target.value)} className={inp} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone"><input value={f.phone} onChange={(e) => set("phone", e.target.value)} className={inp} /></Field>
          <Field label="Email"><input value={f.email} onChange={(e) => set("email", e.target.value)} className={inp} /></Field>
        </div>
        <Field label="Website URL"><input value={f.url} onChange={(e) => set("url", e.target.value)} className={inp} /></Field>
        <Field label="GMB ID (KgMID)"><input value={f.gmbId} onChange={(e) => set("gmbId", e.target.value)} className={inp} /></Field>
        <Field label="Description">
          <textarea value={f.desc} onChange={(e) => set("desc", e.target.value)} rows={4} className={inp} />
        </Field>
        <Field label="Social profiles / sameAs (one URL per line)">
          <textarea value={f.sameAs} onChange={(e) => set("sameAs", e.target.value)} rows={3} className={inp} />
        </Field>
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Generated JSON-LD</div>
        <div className="relative">
          <pre className="rounded-2xl border border-border bg-background/60 p-5 text-xs overflow-x-auto font-mono leading-relaxed">{`<script type="application/ld+json">
${json}
</script>`}</pre>
          <button
            onClick={() => { navigator.clipboard?.writeText(json); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
            className="absolute top-3 right-3 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground"
          >
            {copied ? "✓ Copied" : "Copy JSON"}
          </button>
        </div>
      </div>
    </div>
  );
}

